import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { Decimal, resolveTopupBonuses } from '@mlm/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Skeleton } from '@mlm/ui';
import {
  ecomPaymentMethodQrUrl,
  getEcomPaymentMethods,
  getMe,
  getSlotWallet,
  listMyTopups,
  uploadTopup,
} from '../lib/api';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { fmtCurrency, fmtDateTime } from '../lib/format';

const AMOUNT_RE = /^\d+(\.\d{1,4})?$/;

function statusVariant(status: string): 'success' | 'destructive' | 'warning' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'destructive';
  return 'warning';
}

export function WalletTopupPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { activeSlotId, activeSlotLabel, activeSlotsWithLabel, setActiveSlotId } = useMySlots();
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const topupsQ = useQuery({ queryKey: ['my-topups'], queryFn: listMyTopups });
  const methodsQ = useQuery({
    queryKey: ['ecom-payment-methods'],
    queryFn: () => getEcomPaymentMethods(),
  });
  // The selected slot's wallet — so we can show its open CD debt (if any) and
  // explain that the top-up pays it down first.
  const walletQ = useQuery({
    queryKey: ['wallet', activeSlotId],
    queryFn: () => getSlotWallet(activeSlotId),
    enabled: !!activeSlotId,
  });

  const policy = meQ.data?.tenantPolicy;
  const enabled = policy?.walletTopupEnabled ?? false;
  const settlesDebt = policy?.cdTopupSettlesDebt ?? false;
  const bonusTreatment = policy?.cdTopupBonusTreatment ?? 'principal_only';
  // Memoized: a fresh [] each render would re-run the bonus preview forever.
  const brackets = useMemo(() => policy?.walletTopupBrackets ?? [], [policy]);
  const openDebt =
    settlesDebt && walletQ.data?.debt && !walletQ.data.debt.settledAt ? walletQ.data.debt : null;
  // Top-ups reuse the MANUAL (offline-proof) ecom payment methods — the same
  // ones the member pays with at checkout. A top-up always credits the cash wallet.
  const manualMethods = (methodsQ.data?.methods ?? []).filter((m) => m.settlement === 'manual');

  // Buying assistant answers rides the same proof-of-payment flow; `amount`
  // then means a COUNT of credits and the pesos owed are derived from price.
  const creditsForSale = policy?.aiCreditsForSale === true;
  const creditPrice = policy?.aiCreditPrice ?? null;
  const [buyingCredits, setBuyingCredits] = useState(false);
  const [amount, setAmount] = useState('');
  const [methodCode, setMethodCode] = useState('');
  const [reference, setReference] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Default to the first available method once they load.
  useEffect(() => {
    if (!methodCode && manualMethods.length > 0) setMethodCode(manualMethods[0]!.code);
  }, [manualMethods, methodCode]);

  const amountValid =
    AMOUNT_RE.test(amount) &&
    Number(amount) > 0 &&
    // A third of an answer isn't a thing.
    (!buyingCredits || Number.isInteger(Number(amount)));
  const pesosDue =
    buyingCredits && creditPrice && amountValid
      ? new Decimal(amount).times(creditPrice).toFixed(2)
      : null;
  const selected = manualMethods.find((m) => m.code === methodCode) ?? null;

  // ALL matching brackets apply, so a top-up can earn both a cash and a GC bonus.
  const bonuses = useMemo(() => {
    // Brackets are deposit semantics; a credit purchase is a priced quantity.
    if (!amountValid || buyingCredits) return [];
    try {
      return resolveTopupBonuses(brackets, new Decimal(amount));
    } catch {
      return [];
    }
  }, [amount, amountValid, brackets, buyingCredits]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!activeSlotId) throw new Error('no_active_slot');
      if (!file) throw new Error('missing_file');
      if (!methodCode) throw new Error('invalid_method');
      return uploadTopup({
        slotId: activeSlotId,
        method: methodCode,
        amount,
        currency: buyingCredits ? 'ai_credit' : 'cash',
        reference: reference.trim() || undefined,
        file,
      });
    },
    onSuccess: () => {
      setAmount('');
      setReference('');
      setFile(null);
      setFormError(null);
      qc.invalidateQueries({ queryKey: ['my-topups'] });
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; message?: string } } }).response
        ?.data;
      const code = data?.error ?? (err as Error).message;
      const friendly: Record<string, string> = {
        duplicate_reference: 'That reference was already submitted.',
        invalid_method: 'Pick a valid payment method.',
        slot_not_active: 'Your slot is not active.',
        unsupported_file_type: 'Upload a JPG, PNG, or PDF.',
        file_infected: 'That file failed the virus scan.',
        missing_file: 'Attach your proof of payment.',
        wallet_topup_disabled: 'Top-ups are currently disabled.',
      };
      setFormError(friendly[code] ?? data?.message ?? 'Could not submit your top-up.');
    },
  });

  if (meQ.isLoading) return <Skeleton className="h-64" />;
  if (!enabled) {
    return (
      <Card>
        <CardContent className="flex items-start gap-3 p-6 text-sm text-muted-foreground">
          <Wallet className="mt-0.5 h-5 w-5 shrink-0" />
          <span>Wallet top-ups aren't available right now.</span>
        </CardContent>
      </Card>
    );
  }

  const canSubmit = amountValid && !!file && !!activeSlotId && !!methodCode && !submit.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-h1">
          <Wallet className="h-6 w-6 text-muted-foreground" /> Top up wallet
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pay through one of the methods below, then submit your proof. An admin reviews it and
          credits {activeSlotLabel ?? 'your slot'}'s cash wallet once approved.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">New top-up</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {activeSlotsWithLabel.length > 1 ? (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Which slot</label>
              <div className="flex flex-wrap gap-2">
                {activeSlotsWithLabel.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveSlotId(s.id)}
                    className={
                      'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ' +
                      (activeSlotId === s.id
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted hover:text-foreground')
                    }
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {openDebt ? (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
              <div className="font-medium">
                This slot has {fmtCurrency(openDebt.debtRemaining)} kit debt remaining.
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Your top-up pays that off first — anything above the remaining debt lands in your
                wallet. Once the debt is fully paid, the slot becomes a normal paid slot.
              </p>
            </div>
          ) : null}

          {creditsForSale ? (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">What are you paying for?</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={buyingCredits ? 'outline' : 'default'}
                  size="sm"
                  onClick={() => {
                    setBuyingCredits(false);
                    setAmount('');
                  }}
                >
                  Wallet top-up
                </Button>
                <Button
                  type="button"
                  variant={buyingCredits ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setBuyingCredits(true);
                    setAmount('');
                  }}
                >
                  Assistant answers
                </Button>
              </div>
              {buyingCredits ? (
                <p className="text-xs text-muted-foreground">
                  {fmtCurrency(creditPrice ?? '0')} per answer. They never expire, and your free
                  monthly answers are used first.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-1.5">
            <label className="text-sm font-medium">{buyingCredits ? 'Answers' : 'Amount'}</label>
            <Input
              type="number"
              min={0}
              step={buyingCredits ? 1 : 'any'}
              inputMode={buyingCredits ? 'numeric' : 'decimal'}
              placeholder={buyingCredits ? '10' : '0.00'}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {buyingCredits ? (
              <p className="text-xs text-muted-foreground">
                {pesosDue
                  ? `Pay ${fmtCurrency(pesosDue)} for ${amount} ${Number(amount) === 1 ? 'answer' : 'answers'}.`
                  : 'Whole answers only.'}
              </p>
            ) : null}
            {bonuses.length > 0 && !(openDebt && bonusTreatment === 'no_bonus') ? (
              <p className="text-xs text-success">
                {bonuses
                  .map(
                    (b) =>
                      `+${fmtCurrency(b.bonus.toFixed(2))}${b.bonusCurrency === 'gc' ? ' GC' : ''}`,
                  )
                  .join(' · ')}{' '}
                {openDebt && bonusTreatment === 'include_cash_bonus'
                  ? 'bonus — the cash bonus also goes toward your debt'
                  : 'bonus on approval'}
              </p>
            ) : openDebt && bonusTreatment === 'no_bonus' && bonuses.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                No top-up bonus applies while you&apos;re paying off a kit debt.
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Payment method</label>
            {methodsQ.isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : manualMethods.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No payment methods are set up yet. Ask an admin to add one.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {manualMethods.map((m) => (
                  <button
                    key={m.code}
                    type="button"
                    onClick={() => setMethodCode(m.code)}
                    className={
                      'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ' +
                      (methodCode === m.code
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted hover:text-foreground')
                    }
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pay-to details for the chosen method (account, instructions, QR). */}
          {selected && (selected.accountLabel || selected.instructions || selected.qrKey) ? (
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              {selected.accountLabel ? (
                <div className="font-medium">{selected.accountLabel}</div>
              ) : null}
              {selected.instructions ? (
                <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">
                  {selected.instructions}
                </p>
              ) : null}
              {selected.qrKey ? (
                <img
                  src={ecomPaymentMethodQrUrl(selected.code)}
                  alt="Payment QR"
                  className="mt-2 h-40 w-40 rounded border bg-white object-contain"
                />
              ) : null}
            </div>
          ) : null}

          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Reference number <span className="text-muted-foreground">(optional)</span>
            </label>
            <Input
              placeholder="e.g. GCash confirmation no."
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Proof of payment</label>
            <Input
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>

          {formError ? <p className="text-sm text-destructive">{formError}</p> : null}

          <div className="flex gap-2">
            <Button disabled={!canSubmit} onClick={() => submit.mutate()}>
              {submit.isPending ? 'Submitting…' : 'Submit top-up'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Cancel
            </Button>
          </div>
          {submit.isSuccess ? (
            <p className="text-sm text-success">Submitted — we'll review it shortly.</p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your top-ups</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {topupsQ.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !topupsQ.data?.length ? (
            <p className="p-6 text-sm text-muted-foreground">No top-ups yet.</p>
          ) : (
            <ul className="divide-y">
              {topupsQ.data.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between gap-3 px-6 py-3 text-sm"
                >
                  <div>
                    <div className="font-medium tabular-nums">
                      {fmtCurrency(t.amount)}
                      {t.bonusBreakdown && t.bonusBreakdown.length > 0 ? (
                        <span className="ml-2 text-xs text-success">
                          {t.bonusBreakdown
                            .map(
                              (b) => `+${fmtCurrency(b.amount)}${b.currency === 'gc' ? ' GC' : ''}`,
                            )
                            .join(' · ')}{' '}
                          bonus
                        </span>
                      ) : Number(t.bonusAmount) > 0 ? (
                        <span className="ml-2 text-xs text-success">
                          +{fmtCurrency(t.bonusAmount)} {t.bonusCurrency === 'gc' ? 'GC' : ''} bonus
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t.method} · {fmtDateTime(t.createdAt)}
                      {t.status === 'rejected' && t.rejectionReason
                        ? ` · ${t.rejectionReason}`
                        : ''}
                    </div>
                  </div>
                  <Badge variant={statusVariant(t.status)}>{t.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
